import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, first } from 'rxjs';
import { indicate } from 'src/app/operators';
import { UserService } from 'src/app/core/services/user.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { ValveReleaseOption } from '../../services/valve-release-filter.service';
import {
  CreateReleaseWizardOptionsViewModel,
  CreateReleaseWizardViewModel,
  BusinessUnitViewModel,
  ApplicationViewModel,
  SpecificationViewModel,
  SpecificationType,
  EsraPipingAPIClient,
  ReleaseComponent,
} from 'src/app/shared/models/autogenerated-piping';

/** Mirrors the three steps shown in the wizard stepper header. */
const STEP_ADD_DETAILS: number = 0;
const STEP_ADD_MEMBERS: number = 1;
const STEP_CONFIRMATION: number = 2;

@Component({
  selector: 'app-create-release-wizard',
  templateUrl: './create-release-wizard.component.html',
  styleUrls: ['./create-release-wizard.component.scss'],
  standalone: false,
})
export class CreateReleaseWizardComponent implements OnInit {
  // ── Constants exposed to template ────────────────────────────────────
  readonly STEP_ADD_DETAILS: number = STEP_ADD_DETAILS;
  readonly STEP_ADD_MEMBERS: number = STEP_ADD_MEMBERS;
  readonly STEP_CONFIRMATION: number = STEP_CONFIRMATION;

  /** 0 = PIPE, 1 = VALVE — matches the `ReleaseComponent` backend enum. */
  readonly ReleaseComponent = ReleaseComponent;

  // ── Step tracking ─────────────────────────────────────────────────────
  currentStep: number = STEP_ADD_DETAILS;

  // ── Loading indicators ────────────────────────────────────────────────
  loadingOptions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while the filtered specification list is being fetched from the API. */
  loadingSpecifications$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Inline validation error shown in the Confirmation step banner (null = no error). */
  createErrorMessage: string | null = null;

  // ── Dropdown options (loaded from wizard/options endpoint) ────────────
  businessUnits: BusinessUnitViewModel[] = [];
  applications: ApplicationViewModel[] = [];
  /** Full specification list from wizard options — used as a fallback when the filter call fails. */
  specifications: SpecificationViewModel[] = [];
  /** Filtered specification list from GetForReleaseFilter — drives the spec dropdown. */
  filteredSpecifications: SpecificationViewModel[] = [];

  // ── Step 1: Add Details ───────────────────────────────────────────────
  selectedBusinessUnitId: string = '';
  selectedApplicationId: string = '';
  selectedSpecificationId: string = '';
  releaseName: string = '';
  revisionName: string = '';
  /** false = Valves (ReleaseComponent._1), true = Piping (ReleaseComponent._0). */
  isPiping: boolean = false;
  selectedLinkedValveReleaseId: string = '';
  /** Display name of the currently selected linked valve release, for the Confirmation step. */
  selectedLinkedValveReleaseName: string = '';
  description: string = '';

  // ── Step 2: Add Revision Members ─────────────────────────────────────
  releaseOwners: microsoftgraph.User[] = [];
  internalReviewers: microsoftgraph.User[] = [];
  contributors: microsoftgraph.User[] = [];

  // ── Read-only current user info (set in ngOnInit) ─────────────────────
  currentUserName: string = '';
  private currentUserOid: string = '';

  constructor(
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly userService: UserService,
    private readonly msalService: MsalService,
    private readonly snackBarService: SnackBarService,
    public dialogRef: MatDialogRef<CreateReleaseWizardComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    // Resolve the current user's display name and OID from MSAL.
    const account = this.msalService.instance.getActiveAccount();
    this.currentUserName = account?.name ?? this.userService.getCurrentUser()?.name ?? '';
    this.currentUserOid = (account?.idTokenClaims?.['oid'] as string | undefined) ?? '';

    // Load all dropdown options in a single call.
    this.esraPipingAPIClient.getWizardOptions()
      .pipe(indicate(this.loadingOptions$), first())
      .subscribe({
        next: (options: CreateReleaseWizardOptionsViewModel) => {
          this.businessUnits = options.businessUnits ?? [];
          this.applications = options.applications ?? [];
          this.specifications = options.specifications ?? [];
          // Populate the filtered list immediately using the current component type
          // so the spec dropdown is populated even before the user selects BU/App.
          this.refreshSpecificationFilter();
        },
        error: () => {
          this.snackBarService.showError('Failed to load wizard options. Please try again.');
        },
      });
  }

  // ── Navigation ────────────────────────────────────────────────────────

  /** Validates Step 1 fields and advances to Step 2. */
  goToMembers(): void {
    if (!this.isStep1Valid()) { return; }
    this.currentStep = STEP_ADD_MEMBERS;
  }

  /** Advances from Step 2 to the confirmation summary. */
  goToConfirmation(): void {
    if (!this.isStep2Valid()) { return; }
    this.currentStep = STEP_CONFIRMATION;
  }

  back(): void {
    if (this.currentStep > STEP_ADD_DETAILS) {
      this.createErrorMessage = null;
      this.currentStep--;
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  // ── Component toggle ──────────────────────────────────────────────────

  /**
   * Called when the valves/piping slide toggle changes.
   * Clears the linked valve release when switching back to Valves, and
   * refreshes the specification filter to show only specs matching the new type.
   */
  onComponentToggleChange(): void {
    if (!this.isPiping) {
      // Clear linked valve release when switching back to Valves.
      this.selectedLinkedValveReleaseId = '';
      this.selectedLinkedValveReleaseName = '';
    }
    this.refreshSpecificationFilter();
  }

  /** Called when the Business Unit selection changes to update the filtered spec list. */
  onBusinessUnitChange(): void {
    this.refreshSpecificationFilter();
  }

  /** Called when the Application selection changes to update the filtered spec list. */
  onApplicationChange(): void {
    this.refreshSpecificationFilter();
  }

  /**
   * Calls the backend filter endpoint to fetch only the specifications that match
   * the currently selected BU, Application, and component type (Piping / Valve).
   *
   * Parameters are Guid IDs — the endpoint accepts businessUnitId and applicationId directly.
   * If BU or Application have not yet been chosen, their parameters are omitted and the
   * endpoint returns specs for any BU / Application respectively.
   * On error, falls back to the full spec list from getWizardOptions() so the user is not blocked.
   */
  refreshSpecificationFilter(): void {
    // Pass the selected IDs directly; undefined omits the query-string parameter.
    const businessUnitId: string | undefined = this.selectedBusinessUnitId || undefined;
    const applicationId: string | undefined  = this.selectedApplicationId  || undefined;
    // isPiping === true  → ReleaseComponent._0 (PIPE)  → SpecificationType._0 (Piping)
    // isPiping === false → ReleaseComponent._1 (VALVE) → SpecificationType._1 (Valve)
    const type: SpecificationType = this.isPiping ? SpecificationType._0 : SpecificationType._1;

    this.esraPipingAPIClient.getSpecificationsForReleaseFilter(businessUnitId, applicationId, type)
      .pipe(indicate(this.loadingSpecifications$), first())
      .subscribe({
        next: (specs: SpecificationViewModel[]) => {
          this.filteredSpecifications = specs;
          // If the currently selected spec is no longer in the filtered results, clear it.
          if (this.selectedSpecificationId && !specs.some(s => s.id === this.selectedSpecificationId)) {
            this.selectedSpecificationId = '';
          }
        },
        error: () => {
          // Fall back to the full list so the user is not completely blocked.
          this.filteredSpecifications = [...this.specifications];
        },
      });
  }

  // ── Member management (mirrors create-revision pattern) ──────────────

  addMember(user: microsoftgraph.User, role: 'owner' | 'reviewer' | 'contributor'): void {
    const list: microsoftgraph.User[] = this.getListForRole(role);
    const alreadyAdded: boolean = list.some(u => u.id === user.id);
    if (alreadyAdded) {
      this.snackBarService.showError(`${user.displayName} has already been added.`);
      return;
    }
    list.push(user);
  }

  removeMember(user: microsoftgraph.User, role: 'owner' | 'reviewer' | 'contributor'): void {
    if (role === 'owner') {
      this.releaseOwners = this.releaseOwners.filter(u => u.id !== user.id);
    } else if (role === 'reviewer') {
      this.internalReviewers = this.internalReviewers.filter(u => u.id !== user.id);
    } else {
      this.contributors = this.contributors.filter(u => u.id !== user.id);
    }
  }

  private getListForRole(role: 'owner' | 'reviewer' | 'contributor'): microsoftgraph.User[] {
    if (role === 'owner') { return this.releaseOwners; }
    if (role === 'reviewer') { return this.internalReviewers; }
    return this.contributors;
  }

  // ── Confirmation / Create ─────────────────────────────────────────────

  /**
   * Assembles the `CreateReleaseWizardViewModel` from the current form state.
   * Centralised here so both the preview and the final create call use the
   * exact same payload without duplication.
   */
  private buildWizardViewModel(): CreateReleaseWizardViewModel {
    return new CreateReleaseWizardViewModel({
      businessUnitId: this.selectedBusinessUnitId,
      applicationId: this.selectedApplicationId,
      specificationId: this.selectedSpecificationId || undefined,
      name: this.releaseName,
      revision: this.revisionName,
      // ReleaseComponent._0 = PIPE, ReleaseComponent._1 = VALVE
      component: this.isPiping ? ReleaseComponent._0 : ReleaseComponent._1,
      linkedValveReleaseId: this.isPiping ? (this.selectedLinkedValveReleaseId || undefined) : undefined,
      description: this.description || undefined,
      creatorOid: this.currentUserOid,
      releaseOwners: this.releaseOwners.map(u => u.id),
      internalReviewers: this.internalReviewers.map(u => u.id),
      contributors: this.contributors.map(u => u.id),
    });
  }

  /**
   * Sends the wizard payload to `POST /Release/wizard/create`.
   * On a 400 Bad Request the BE returns a plain-text body describing the specific
   * validation failure (e.g. duplicate name); that text is read from `err.error`
   * (an Angular `HttpErrorResponse`) and shown in the inline banner on the
   * Confirmation step so the user can act on it immediately.
   */
  createRelease(): void {
    this.createErrorMessage = null;
    const wizardVm: CreateReleaseWizardViewModel = this.buildWizardViewModel();

    this.esraPipingAPIClient.createReleaseFromWizard(wizardVm)
      .pipe(indicate(this.saving$), first())
      .subscribe({
        next: () => {
          this.snackBarService.showInfo('Release created successfully.');
          this.dialogRef.close(true);
        },
        error: (err) => {
          // NSwag's generated throwException() passes `result` directly to _observableThrow
          // when result is non-null. For 400, result is the raw plain-text body, so `err`
          // itself is the string. For other error shapes, fall back to ApiException.response
          // or err.message.
          let message: string | null = null;
          if (typeof err === 'string') {
            message = err;
          } else if (typeof err?.response === 'string' && err.response) {
            message = err.response;
          } else if (typeof err?.error === 'string' && err.error) {
            message = err.error;
          } else if (typeof err?.message === 'string' && err.message) {
            message = err.message;
          }
          this.createErrorMessage = message || 'Failed to create the release. Please check the form and try again.';
        },
      });
  }

  // ── Validation helpers ────────────────────────────────────────────────

  isStep1Valid(): boolean {
    const baseValid: boolean =
      !!this.selectedBusinessUnitId &&
      !!this.selectedApplicationId &&
      !!this.releaseName.trim() &&
      !!this.revisionName.trim();

    // When Piping is selected, a linked valve release is required.
    if (this.isPiping) {
      return baseValid && !!this.selectedLinkedValveReleaseId;
    }
    return baseValid;
  }

  isStep2Valid(): boolean {
    return this.releaseOwners.length > 0 && this.internalReviewers.length > 0;
  }

  // ── Display helpers ───────────────────────────────────────────────────

  getBusinessUnitName(): string {
    return this.businessUnits.find(bu => bu.id === this.selectedBusinessUnitId)?.name ?? '';
  }

  getApplicationName(): string {
    return this.applications.find(app => app.id === this.selectedApplicationId)?.name ?? '';
  }

  getSpecificationName(): string {
    // Search the filtered list first, then fall back to the full list in case the
    // selected spec was cleared from the filter but the confirmation step still needs to show it.
    const spec: SpecificationViewModel | undefined =
      this.filteredSpecifications.find(s => s.id === this.selectedSpecificationId) ??
      this.specifications.find(s => s.id === this.selectedSpecificationId);
    if (!spec) { return ''; }
    return this.getSpecOptionLabel(spec);
  }

  /**
   * Builds the display label for a specification option in the dropdown and
   * confirmation summary. Prefers "SpecNumber — SpecTitle" for new-style records;
   * falls back to the legacy name for records that pre-date the new fields.
   * @param spec The specification view model to format.
   * @returns A human-readable label string.
   */
  getSpecOptionLabel(spec: SpecificationViewModel): string {
    if (spec.specificationNumber) {
      const title: string = spec.specificationTitle || spec.name || '';
      return title ? `${spec.specificationNumber} \u2014 ${title}` : spec.specificationNumber;
    }
    return spec.name ?? '';
  }

  getLinkedValveReleaseName(): string {
    return this.selectedLinkedValveReleaseName;
  }

  /** Called when the linked valve release dropdown selection changes; stores the display name for the Confirmation step. */
  onLinkedValveReleaseChange(release: ValveReleaseOption | null): void {
    this.selectedLinkedValveReleaseName = release?.name ?? '';
  }

  /** Returns a comma-separated list of display names for the given user list. */
  getUserNames(users: microsoftgraph.User[]): string {
    return users.length > 0 ? users.map(u => u.displayName).join(', ') : '—';
  }
}
